import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, CheckCircle, LocalOffer, OpenInNew, WorkspacePremium } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { startCheckout, openBillingPortal } from "../services/billingService";
import {
  getMySubscriptions,
  getPlanCatalog,
  isSubscriptionAccessActive,
} from "../services/subscriptionService";
import { redeemPromoCode } from "../services/promoCodesService";
import colors from "../design-system/colors";
import usePageTitle from "../hooks/usePageTitle";
import { getLicenseFeature } from "../components/license/licenseFeatureCatalog";

const STATUS_COLORS = {
  active: "success",
  trialing: "info",
  past_due: "warning",
  canceled: "default",
  unpaid: "error",
  incomplete: "warning",
  incomplete_expired: "error",
};
const INDIVIDUAL_PROFESSIONAL_ROLES = [
  ["coach", "Entrenador/a"],
  ["analyst", "Analista"],
];

const normalizeIndividualProfessionalRole = (role) =>
  INDIVIDUAL_PROFESSIONAL_ROLES.some(([value]) => value === role)
    ? role
    : "coach";

const formatPrice = (price) => {
  if (price.unit_amount == null) return "Precio a consultar";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: price.currency.toUpperCase(),
  }).format(price.unit_amount / 100);
};

function PlanCard({
  plan,
  billingInterval,
  purchasing,
  activeSubscription,
  onPurchase,
  onManage,
}) {
  const price = plan.prices.find(
    (candidate) => candidate.billing_interval === billingInterval
  );

  return (
    <Card
      sx={{
        height: "100%",
        border: 1, borderColor: 'divider',
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <WorkspacePremium color="primary" />
          <Chip
            size="small"
            label={plan.scope === "club" ? "Club" : "Individual"}
            color={plan.scope === "club" ? "primary" : "default"}
          />
        </Stack>
        <Typography variant="h5" fontWeight={800} sx={{ mt: 2 }}>
          {plan.display_name}
        </Typography>
        <Typography color="text.secondary" sx={{ minHeight: 48, mt: 1 }}>
          {plan.description}
        </Typography>
        <Typography variant="h4" fontWeight={800} color="primary" sx={{ mt: 2 }}>
          {price ? formatPrice(price) : "No disponible"}
        </Typography>
        {price && (
          <Typography variant="body2" color="text.secondary">
            por {billingInterval === "month" ? "mes" : "año"}
          </Typography>
        )}
        {plan.scope === "club" && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {plan.included_staff_seats} plazas incluidas
          </Typography>
        )}
        <Stack spacing={0.75} sx={{ mt: 2, flex: 1 }}>
          {(plan.features || []).map((feature) => (
            <Stack key={feature} direction="row" spacing={1} alignItems="flex-start">
              <CheckCircle color="success" sx={{ fontSize: 18, mt: 0.2 }} />
              <Typography variant="body2">{feature}</Typography>
            </Stack>
          ))}
        </Stack>
        <Button
          fullWidth
          variant="contained"
          disabled={!activeSubscription && (!price || purchasing)}
          onClick={() =>
            activeSubscription ? onManage(activeSubscription) : onPurchase(plan)
          }
          sx={{ mt: 3 }}
        >
          {activeSubscription
            ? "Cambiar plan en Stripe"
            : purchasing
              ? "Redirigiendo..."
              : "Contratar"}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function LicenciasPageModern() {
  usePageTitle("Licencias");
  const {
    user,
    profile,
    updateProfile,
    refreshAuthorization,
    authorizationError,
    clearAuthorizationError,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requiredPermission = searchParams.get("required");
  const returnTo = searchParams.get("returnTo");
  const requestedFeature = requiredPermission ? getLicenseFeature(requiredPermission) : null;
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [scope, setScope] = useState("individual");
  const [professionalRole, setProfessionalRole] = useState(() =>
    normalizeIndividualProfessionalRole(user?.professionalRole)
  );
  const [billingInterval, setBillingInterval] = useState("month");
  const [seatQuantity, setSeatQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [savingProfessionalRole, setSavingProfessionalRole] = useState(false);
  const [error, setError] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [redeemingPromo, setRedeemingPromo] = useState(false);
  const [promoFeedback, setPromoFeedback] = useState(null);
  const [pendingPromoChange, setPendingPromoChange] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [nextPlans, nextSubscriptions] = await Promise.all([
        getPlanCatalog(),
        getMySubscriptions(),
      ]);
      setPlans(nextPlans);
      setSubscriptions(nextSubscriptions);
    } catch (loadError) {
      setError(loadError.message || "No se pudieron cargar los planes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setProfessionalRole(normalizeIndividualProfessionalRole(user?.professionalRole));
  }, [user?.professionalRole]);

  const visiblePlans = useMemo(
    () =>
      plans.filter(
        (plan) =>
          plan.scope === scope
          && (
            scope !== "individual"
            || !plan.compatible_professional_roles?.length
            || plan.compatible_professional_roles.includes(professionalRole)
          )
      ),
    [plans, professionalRole, scope]
  );
  const activeSubscriptions = useMemo(
    () => subscriptions.filter(isSubscriptionAccessActive),
    [subscriptions]
  );
  const selectedActiveSubscription = useMemo(() => {
    if (scope === "individual") {
      return activeSubscriptions.find((subscription) => subscription.scope === "individual");
    }
    return activeSubscriptions.find((subscription) => subscription.scope === "club");
  }, [activeSubscriptions, scope]);
  const subscriptionPlanName = useCallback(
    (subscription) =>
      plans.find(
        (plan) =>
          plan.plan_key === subscription.plan_key && plan.scope === subscription.scope
      )?.display_name
      || (subscription.plan_key === "trial-full" ? "Prueba gratuita de un mes" : subscription.plan_key),
    [plans]
  );
  const planName = useCallback(
    (planKey) => plans.find((plan) => plan.plan_key === planKey)?.display_name || planKey,
    [plans]
  );
  const historicalSubscriptions = useMemo(
    () => subscriptions.filter((subscription) => !isSubscriptionAccessActive(subscription)),
    [subscriptions]
  );

  const handlePurchase = async (plan) => {
    setPurchasing(true);
    setError("");
    try {
      await startCheckout({
        planKey: plan.plan_key,
        billingInterval,
        scope,
        seatQuantity: scope === "club" ? seatQuantity : 1,
      });
    } catch (checkoutError) {
      setError(checkoutError.message || "No se pudo iniciar el pago.");
      setPurchasing(false);
    }
  };

  const handleManageBilling = async (subscription) => {
    setPurchasing(true);
    setError("");
    try {
      await openBillingPortal();
    } catch (portalError) {
      setError(portalError.message || "No se pudo abrir la facturación.");
      setPurchasing(false);
    }
  };

  const applyPromoCode = async (code, confirmPlanChange = false) => {
    setRedeemingPromo(true);
    setError("");
    setPromoFeedback(null);
    try {
      const result = await redeemPromoCode({
        code,
        userId: user.id,
        userEmail: user.email,
        confirmPlanChange,
      });
      setPendingPromoChange(null);
      setPromoCode("");
      try {
        await Promise.all([load(), refreshAuthorization()]);
      } catch (refreshError) {
        setPromoFeedback({
          severity: "error",
          message: `El código se aplicó, pero no se pudieron actualizar los permisos: ${refreshError.message || "recarga la página."}`,
        });
        return;
      }
      setPromoFeedback({
        severity: "success",
        message: result.renewsAutomatically
          ? `Código aplicado desde hoy. La licencia se renovará con su tarifa normal después del ${new Date(result.newPeriodEnd).toLocaleDateString("es-ES")}.`
          : `Código aplicado desde hoy. La licencia estará activa hasta ${new Date(result.newPeriodEnd).toLocaleDateString("es-ES")}.`,
      });
    } catch (redeemError) {
      if (redeemError.details?.code === "PLAN_CHANGE_CONFIRMATION_REQUIRED") {
        setPendingPromoChange({ ...redeemError.details, promoCode: code });
        return;
      }
      setPromoFeedback({
        severity: "error",
        message: redeemError.message || "No se pudo aplicar el código promocional.",
      });
    } finally {
      setRedeemingPromo(false);
    }
  };

  const handleRedeemPromo = async (event) => {
    event.preventDefault();
    const code = promoCode.trim();
    if (!code || !user?.id) return;
    await applyPromoCode(code);
  };

  const handleConfirmPromoChange = async () => {
    const pendingChange = pendingPromoChange;
    if (!pendingChange) return;
    await applyPromoCode(pendingChange.promoCode, true);
  };

  const handleProfessionalRoleChange = async (event) => {
    const nextProfessionalRole = event.target.value;
    const previousProfessionalRole = professionalRole;
    setProfessionalRole(nextProfessionalRole);
    setSavingProfessionalRole(true);
    setError("");
    try {
      await updateProfile({
        fullName: profile?.full_name || user?.nombre || "",
        professionalRole: nextProfessionalRole,
      });
    } catch (profileError) {
      setProfessionalRole(previousProfessionalRole);
      setError(profileError.message || "No se pudo actualizar el perfil profesional.");
    } finally {
      setSavingProfessionalRole(false);
    }
  };

  const handleReturn = () => {
    navigate(returnTo || "/dashboard");
    window.setTimeout(() => {
      try {
        const saved = JSON.parse(sessionStorage.getItem("licenseUpgradeReturn") || "null");
        if (saved?.pathname === returnTo && Number.isFinite(saved.scrollY)) {
          window.scrollTo({ top: saved.scrollY, behavior: "auto" });
        }
        sessionStorage.removeItem("licenseUpgradeReturn");
      } catch {
        window.scrollTo({ top: 0, behavior: "auto" });
      }
    }, 0);
  };

  return (
    <Box className="wp-page" component="section" sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 4 }, py: 4 }}>
      <Stack spacing={3}>
        <Box className="wp-heading">
          {returnTo && (
            <Button
              variant="text"
              startIcon={<ArrowBack />}
              onClick={handleReturn}
              sx={{ mb: 1, minHeight: 44 }}
            >
              Volver a la función
            </Button>
          )}
          <Typography component="h1" variant="h4" fontWeight={800}>Licencias y facturación</Typography>
          <Typography color="text.secondary">
            Contrata planes, consulta límites y gestiona tus suscripciones mediante Stripe.
          </Typography>
        </Box>
        {requestedFeature && (
          <Alert severity="info" icon={<WorkspacePremium />}>
            <strong>{requestedFeature.title}:</strong> {requestedFeature.description}
            {requestedFeature.plans.length > 0
              ? ` Planes recomendados: ${requestedFeature.plans.join(", ")}.`
              : ""}
          </Alert>
        )}
        {searchParams.get("checkout") === "success" && (
          <Alert severity="success">
            El pago se ha completado. La suscripción se actualizará en unos instantes.
          </Alert>
        )}
        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
        {authorizationError && (
          <Alert severity="error" onClose={clearAuthorizationError}>
            No se pudo aplicar el código promocional pendiente: {authorizationError}
          </Alert>
        )}

        <Card component="form" onSubmit={handleRedeemPromo} variant="outlined" sx={{ borderColor: 'secondary.main', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 213, 79, 0.12)' : theme.palette.secondary.light }}>
          <CardContent>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
              <LocalOffer sx={{ color: colors.secondary[800] }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={800}>¿Tienes un código promocional?</Typography>
                <Typography variant="body2" color="text.secondary">
                  Introdúcelo para activar el beneficio en tu cuenta.
                </Typography>
              </Box>
              <TextField
                className="wp-promo-code"
                size="small"
                label="Código promocional"
                value={promoCode}
                onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
                disabled={redeemingPromo}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={!promoCode.trim() || redeemingPromo}
                startIcon={redeemingPromo ? <CircularProgress size={16} color="inherit" /> : <LocalOffer />}
              >
                Aplicar código
              </Button>
            </Stack>
            {promoFeedback && (
              <Alert
                severity={promoFeedback.severity}
                sx={{ mt: 2 }}
                onClose={() => setPromoFeedback(null)}
              >
                {promoFeedback.message}
              </Alert>
            )}
          </CardContent>
        </Card>

        <Dialog
          open={Boolean(pendingPromoChange)}
          onClose={redeemingPromo ? undefined : () => setPendingPromoChange(null)}
          aria-labelledby="promo-plan-change-title"
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle id="promo-plan-change-title">Cambiar licencia con el código</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <DialogContentText>
                Vas a sustituir <strong>{planName(pendingPromoChange?.currentPlanKey)}</strong> por{" "}
                <strong>{pendingPromoChange?.targetPlanName || planName(pendingPromoChange?.targetPlanKey)}</strong>.
              </DialogContentText>
              <Alert severity="warning">
                El tiempo restante de la licencia actual no se acumulará. La nueva licencia y el periodo
                promocional de {pendingPromoChange?.durationMonths} meses comenzarán en el momento de confirmar.
              </Alert>
              <DialogContentText>
                {pendingPromoChange?.renewsAutomatically
                  ? "Cuando termine la promoción, Stripe renovará automáticamente la nueva licencia con su tarifa normal."
                  : "Esta cuenta no tiene una suscripción de pago asociada. Al terminar la promoción tendrás que contratar una licencia para mantener el acceso."}
              </DialogContentText>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setPendingPromoChange(null)}
              disabled={redeemingPromo}
              sx={{ minHeight: 44 }}
            >
              Mantener licencia actual
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmPromoChange}
              disabled={redeemingPromo}
              startIcon={redeemingPromo ? <CircularProgress size={16} color="inherit" /> : <LocalOffer />}
              sx={{ minHeight: 44 }}
            >
              Cambiar y aplicar código
            </Button>
          </DialogActions>
        </Dialog>

        {activeSubscriptions.length > 0 && (
          <Card
            sx={{
              border: 2, borderColor: 'success.main',
              borderRadius: 3,
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.12)' : theme.palette.success.light,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckCircle color="success" />
                      <Typography variant="h5" fontWeight={800}>
                        Licencia activa
                      </Typography>
                    </Stack>
                    <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                      Tu acceso está habilitado con el plan y las condiciones indicados abajo.
                    </Typography>
                  </Box>
                  {activeSubscriptions.length === 1 && activeSubscriptions[0].stripe_customer_id && (
                    <Button
                      variant="contained"
                      color="success"
                      endIcon={<OpenInNew />}
                      onClick={() => handleManageBilling(activeSubscriptions[0])}
                    >
                      Gestionar en Stripe
                    </Button>
                  )}
                </Stack>
                <Grid container spacing={1.5}>
                  {activeSubscriptions.map((subscription) => (
                    <Grid key={subscription.id} item xs={12} md={6}>
                      <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 2 }}>
                        <Typography fontWeight={800}>
                          {subscriptionPlanName(subscription)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {subscription.scope === "club"
                            ? `Plan de club · ${subscription.seats_total} plazas`
                            : "Licencia profesional individual"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {subscription.cancel_at_period_end
                            ? "Cancelación programada al final del periodo"
                            : `Renovación: ${subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString("es-ES") : "pendiente"}`}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        )}

        <Card variant="outlined">
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de plan"
                  value={scope}
                  onChange={(event) => setScope(event.target.value)}
                >
                  <MenuItem value="individual">Profesional individual</MenuItem>
                  <MenuItem value="club">Club</MenuItem>
                </TextField>
              </Grid>
              {scope === "individual" && (
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Perfil profesional"
                    value={professionalRole}
                    onChange={handleProfessionalRoleChange}
                    disabled={savingProfessionalRole}
                    helperText="Define las licencias individuales y permisos compatibles."
                  >
                    {INDIVIDUAL_PROFESSIONAL_ROLES.map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Facturación"
                  value={billingInterval}
                  onChange={(event) => setBillingInterval(event.target.value)}
                >
                  <MenuItem value="month">Mensual</MenuItem>
                  <MenuItem value="year">Anual</MenuItem>
                </TextField>
              </Grid>
              {scope === "club" && (
                <>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Plazas"
                      value={seatQuantity}
                      inputProps={{ min: 1, max: 500 }}
                      onChange={(event) => setSeatQuantity(Math.max(1, Number(event.target.value) || 1))}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>Planes disponibles</Typography>
          {selectedActiveSubscription && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Cambio de plan:</strong> se gestiona en Stripe. El cambio se aplica
              de inmediato, Stripe calcula el ajuste proporcional y tu fecha de renovación
              se mantiene. No se crea una segunda licencia.
            </Alert>
          )}
          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", minHeight: 180 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {visiblePlans.map((plan) => (
                <Grid key={`${plan.scope}-${plan.plan_key}`} item xs={12} md={6} lg={4}>
                  <PlanCard
                    plan={plan}
                    billingInterval={billingInterval}
                    purchasing={purchasing}
                    activeSubscription={selectedActiveSubscription}
                    onPurchase={handlePurchase}
                    onManage={handleManageBilling}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>Historial y estados pendientes</Typography>
          {historicalSubscriptions.length === 0 ? (
            <Alert severity="info">No hay suscripciones anteriores ni pendientes.</Alert>
          ) : (
            <Stack spacing={1.5}>
              {historicalSubscriptions.map((subscription) => (
                <Card key={subscription.id} variant="outlined">
                  <CardContent>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography fontWeight={800}>{subscriptionPlanName(subscription)}</Typography>
                          <Chip
                            size="small"
                            label={subscription.status}
                            color={STATUS_COLORS[subscription.status] || "default"}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {subscription.scope === "club"
                            ? `Plan de club · ${subscription.seats_total} plazas`
                            : `Licencia individual de ${user?.email || "usuario"}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {subscription.cancel_at_period_end
                            ? "Cancelación programada al final del periodo"
                            : `Renovación: ${subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString("es-ES") : "pendiente"}`}
                        </Typography>
                      </Box>
                      {subscription.stripe_customer_id && (
                        <Button
                          variant="outlined"
                          endIcon={<OpenInNew />}
                          onClick={() => handleManageBilling(subscription)}
                        >
                          Gestionar facturación
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
